import {useEffect,useMemo,useState,type FormEvent} from 'react';
type Discount={
 id:string;title:string;code:string;description:string;channel:'online'|'internal';
 enabled:boolean;newOnly:boolean;mailingListOnly:boolean;uses:number;
 useLimit:number|null;perCustomerLimit:number|null;createdBy:string;
 employeeInstructions:string;receiptMessage:string;availableFrom:string|null;
 availableUntil:string|null;minPurchaseCents:number|null;
 serviceCreditExpiryDays:number|null;accountCreditExpiryDays:number|null;version:number;
};
type Body={ok:boolean;data?:{discounts:Discount[];canEdit:boolean};
 error?:{code:string}};
const blank=():Discount=>({id:'',title:'',code:'',description:'',
 channel:'internal',enabled:true,newOnly:false,mailingListOnly:false,uses:0,
 useLimit:null,perCustomerLimit:null,createdBy:'',employeeInstructions:'',
 receiptMessage:'',availableFrom:null,availableUntil:null,minPurchaseCents:null,
 serviceCreditExpiryDays:null,accountCreditExpiryDays:null,version:1});
const raw=(n:number|null)=>n===null?'':String(n);
const dollars=(n:number|null)=>n===null?'':String(n/100);
const optionalInteger=(v:string)=>v.trim()===''?null:Number(v);
export function DiscountCodesWorkspace({locationCode}:{locationCode:string}){
 const [discounts,setDiscounts]=useState<Discount[]>([]);
 const [canEdit,setCanEdit]=useState(false);
 const [loadState,setLoadState]=useState<'loading'|'ready'|'error'>('loading');
 const [status,setStatus]=useState('');
 const [selected,setSelected]=useState<Discount|null>(null);
 const [price,setPrice]=useState('');
 const [saving,setSaving]=useState(false);
 const [filter,setFilter]=useState('');
 const [statusFilter,setStatusFilter]=useState<'enabled'|'disabled'|'all'>('enabled');
 const [channel,setChannel]=useState<'all'|'online'|'internal'>('all');
 const [membership,setMembership]=useState(false);
 const load=async(signal?:AbortSignal)=>{
   const res=await fetch('/api/v1/staff/discount-code-setup?'+
    new URLSearchParams({location:locationCode}),{
    credentials:'same-origin',cache:'no-store',signal});
   const body=await res.json() as Body;
   if(!res.ok||!body.ok||!body.data)throw Error('Discount Codes unavailable.');
   if(signal?.aborted)return;
   setDiscounts(body.data.discounts);
   setCanEdit(body.data.canEdit);
 };
 useEffect(()=>{
   const controller=new AbortController();setSelected(null);setStatus('');
   setLoadState('loading');
   void load(controller.signal).then(()=>{
     if(!controller.signal.aborted)setLoadState('ready');
   }).catch(()=>{if(!controller.signal.aborted)setLoadState('error');});
   return ()=>controller.abort();
 },[locationCode]);
 const results=useMemo(()=>discounts.filter(d=>
   (statusFilter==='all'||d.enabled===(statusFilter==='enabled'))&&
   (channel==='all'||d.channel===channel)&&
   (!membership||d.description.toLowerCase().includes('membership'))&&
   (!filter.trim()||[d.title,d.code,d.description]
     .some(x=>x.toLowerCase().includes(filter.trim().toLowerCase())))),
   [discounts,statusFilter,channel,membership,filter]);
 const edit=(d:Discount)=>{setSelected({...d});setPrice(dollars(d.minPurchaseCents));
   setStatus('');};
 const change=(patch:Partial<Discount>)=>setSelected(prev=>prev?{...prev,...patch}:prev);
 const save=async(event:FormEvent)=>{
   event.preventDefault();if(!selected||saving)return;
   const val=price.trim()===''?null:Number(price);
   if(val!==null&&(!Number.isFinite(val)||val<0||
     Math.abs(Math.round(val*100)-val*100)>0.00001)){
     setStatus('Minimum purchase must be a valid dollar amount.');return;
   }
   const values={location:locationCode,
     ...(selected.id?{id:selected.id,expectedVersion:selected.version}:{}),
     title:selected.title,code:selected.code,description:selected.description,
     channel:selected.channel,enabled:selected.enabled,newOnly:selected.newOnly,
     mailingListOnly:selected.mailingListOnly,useLimit:selected.useLimit,
     perCustomerLimit:selected.perCustomerLimit,
     employeeInstructions:selected.employeeInstructions,
     receiptMessage:selected.receiptMessage,availableFrom:selected.availableFrom||null,
     availableUntil:selected.availableUntil||null,
     minPurchaseCents:val===null?null:Math.round(val*100),
     serviceCreditExpiryDays:selected.serviceCreditExpiryDays,
     accountCreditExpiryDays:selected.accountCreditExpiryDays};
   setSaving(true);setStatus('');
   try{
     const res=await fetch('/api/v1/staff/discount-code-setup',{
       method:selected.id?'PATCH':'POST',credentials:'same-origin',
       headers:{'content-type':'application/json'},body:JSON.stringify(values)});
     if(!res.ok){
       const body=await res.json().catch(()=>null) as Body|null;
       if(body?.error?.code==='DISCOUNT_STALE_OR_MISSING')
         throw Error('This code was changed elsewhere. Reload and retry.');
       if(body?.error?.code==='DISCOUNT_CODE_ALREADY_EXISTS')
         throw Error('That discount code is already in use.');
       throw Error('Discount Code could not be saved ('+res.status+').');
     }
     await load();setSelected(null);setStatus('Discount Code saved.');
   }catch(e){setStatus(e instanceof Error?e.message:'Discount Code could not be saved.');}
   finally{setSaving(false);}
 };
 const number=(key:'useLimit'|'perCustomerLimit'|'serviceCreditExpiryDays'|
   'accountCreditExpiryDays',label:string)=><label>{label}
     <input type="number" min={0} step={1} value={raw(selected?.[key]??null)}
       onChange={e=>change({[key]:optionalInteger(e.target.value)})}/></label>;
 return <section className="ecfs-discount-setup" aria-label="Discount Codes setup">
   <header className="ecfs-products-heading"><h1>Discount Codes</h1></header>
   {loadState==='loading'&&<p role="status">Loading Discount Codes…</p>}
   {loadState==='error'&&<p role="alert">Discount Codes could not be loaded.</p>}
   {status&&<p role={status==='Discount Code saved.'?'status':'alert'}>{status}</p>}
   {loadState==='ready'&&selected&&canEdit?<form aria-label="Edit Discount Code"
       className="ecfs-discount-editor" onSubmit={e=>void save(e)}>
     <button type="button" onClick={()=>{setSelected(null);setStatus('');}}>
       ← Discount Codes</button>
     <div className="ecfs-discount-editor-grid">
       <label>Name (for internal use only)
         <input required maxLength={320} value={selected.title}
           onChange={e=>change({title:e.target.value})}/></label>
       <label>Code
         <input required maxLength={160} value={selected.code}
           onChange={e=>change({code:e.target.value})}/></label>
       <label>Employee Instructions (optional)
         <textarea value={selected.employeeInstructions}
           onChange={e=>change({employeeInstructions:e.target.value})}/></label>
       <label>Sales Receipt Message for Customers (optional)
         <textarea value={selected.receiptMessage}
           onChange={e=>change({receiptMessage:e.target.value})}/></label>
     </div>
     <h3>Usage Limits</h3>
     <label><input type="checkbox" checked={selected.channel==='online'}
       onChange={e=>change({channel:e.target.checked?'online':'internal'})}/>
       Usable by Customers Online</label>
     <label><input type="checkbox" checked={selected.newOnly}
       onChange={e=>change({newOnly:e.target.checked})}/>New Customers Only</label>
     <label><input type="checkbox" checked={selected.mailingListOnly}
       onChange={e=>change({mailingListOnly:e.target.checked})}/>
       Customer Must Be On Mailing List</label>
     <div className="ecfs-discount-editor-grid">
       {number('useLimit','Overall Usage Limit (optional)')}
       {number('perCustomerLimit','Per Customer Usage Limit (optional)')}
       <label>Usable From (optional)
         <input type="date" value={selected.availableFrom??''}
           onChange={e=>change({availableFrom:e.target.value||null})}/></label>
       <label>End Date (optional)
         <input type="date" value={selected.availableUntil??''}
           onChange={e=>change({availableUntil:e.target.value||null})}/></label>
     </div>
     <h3>Purchase Requirements</h3>
     <label>Minimum Purchase Price (optional)
       <input type="number" min={0} step="0.01" value={price}
         onChange={e=>setPrice(e.target.value)}/></label>
     <h3>Discounts Given</h3>
     <label>Discount rules
       <textarea rows={6} value={selected.description}
         onChange={e=>change({description:e.target.value})}/></label>
     <div className="ecfs-discount-editor-grid">
       {number('serviceCreditExpiryDays','Service Credits purchased expire after days (optional)')}
       {number('accountCreditExpiryDays','Credit purchased expires after days (optional)')}
     </div>
     <label><input type="checkbox" checked={selected.enabled}
       onChange={e=>change({enabled:e.target.checked})}/>Enabled</label>
     <div className="ecfs-products-editor-actions">
       <button disabled={saving} type="submit">{saving?'Saving…':'Save Discount Code'}</button>
       <button disabled={saving} type="button" onClick={()=>setSelected(null)}>cancel</button>
     </div>
   </form>:null}
   {loadState==='ready'&&!selected&&<>
     <div className="ecfs-products-toolbar">
       {canEdit&&<button className="ecfs-products-add" type="button"
         onClick={()=>edit(blank())}>Create A Discount Code</button>}
     </div>
     <div className="ecfs-products-filters">
       {(['enabled','disabled','all'] as const).map(v=>
         <button key={v} type="button" className={statusFilter===v?'selected':''}
           onClick={()=>setStatusFilter(v)}>{v[0].toUpperCase()+v.slice(1)}</button>)}
       {(['online','internal'] as const).map(v=>
         <button key={v} type="button" className={channel===v?'selected':''}
           onClick={()=>setChannel(current=>current===v?'all':v)}>
           {v[0].toUpperCase()+v.slice(1)}</button>)}
       <button type="button" className={membership?'selected':''}
         onClick={()=>setMembership(v=>!v)}>Used by Memberships</button>
       <input type="search" aria-label="Filter Discount Codes"
         placeholder="Filter" value={filter} onChange={e=>setFilter(e.target.value)}/>
     </div>
     <div className="ecfs-products-table-scroll">
       <table className="ecfs-products-table">
         <thead><tr><th>Public</th><th>Code</th><th>Name</th><th>Discount</th>
           <th>Req's</th><th>Uses</th><th>Limit</th>
           <th>per customer</th><th>Created By</th></tr></thead>
         <tbody>{results.map(d=><tr key={d.id}>
           <td>{d.channel==='online'?'🌐':''}</td>
           <td><button type="button" disabled={!canEdit}
             className="ecfs-products-header-link" onClick={()=>edit(d)}>{d.code}</button></td>
           <td><button type="button" disabled={!canEdit}
             className="ecfs-products-header-link" onClick={()=>edit(d)}>{d.title}</button></td>
           <td className="ecfs-discount-rule">{d.description}</td>
           <td>{d.newOnly?'New Only':'none'}</td><td>{d.uses}</td>
           <td>{d.useLimit??'—'}</td><td>{d.perCustomerLimit??'—'}</td>
           <td>{d.createdBy}</td>
         </tr>)}</tbody>
       </table>
       {!results.length&&<p>No matching Discount Codes.</p>}
     </div>
   </>}
 </section>;
}
